import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import HowToPlay from "../components/HowToPlay";
import InteractiveTutorial, { hasSeenTutorial } from "../components/InteractiveTutorial";
import OutOfChipsModal from "../components/OutOfChipsModal";
import RedeemInviteModal, { shouldOfferRedeem, markRedeemed } from "../components/RedeemInviteModal";
import InviteFriendsModal from "../components/InviteFriendsModal";
import { mountBanner, unmountBanner, shouldShowBanner, BANNER_HEIGHT_PX, isAdsRemovedActive } from "../lib/banner";
import DailyStreak from "../components/DailyStreak";
import SignInOffer from "../components/SignInOffer";
import CosmeticsModal from "../components/CosmeticsModal";
import DailyDealBanner from "../components/DailyDealBanner";
import AdFreeUpsellModal from "../components/AdFreeUpsellModal";
import AvatarFrame from "../components/AvatarFrame";
import WatchAdButton from "../components/WatchAdButton";
import DebugOverlay, { enableDebug } from "../components/DebugOverlay";
import axios from "axios";
import { fetchOrCreateProfile, updateProfile, getStoredName, fetchLeaderboard, API, getOrCreateUserId, getEmergentRedirectUrl, getMe, logout, isAuthed } from "../lib/api";
import { signInWithGoogleNative, signOutGoogleNative, rehydrateReady } from "../lib/authNative";
import { PLAYERS } from "../lib/quattro";
import { sfx, isMuted, setMuted } from "../lib/sound";
import { getInitials } from "../lib/avatar";
import { track, identify } from "../lib/analytics";

const DIFFICULTIES = [
  { key: "easy",   label: "Easy",   desc: "Random bots — pure luck practice" },
  { key: "medium", label: "Medium", desc: "Bots block & extend lines" },
  { key: "hard",   label: "Hard",   desc: "Bots look ahead & exploit forks" },
];

function getDifficulty() {
  try { return localStorage.getItem("quattro_difficulty") || "medium"; } catch { return "medium"; }
}
function setDifficulty(v) {
  try { localStorage.setItem("quattro_difficulty", v); } catch {}
}

export default function Lobby() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const profileRef = React.useRef(null);
  // Always carry the latest profile for handlers that fire after async refetches.
  React.useEffect(() => { profileRef.current = profile; }, [profile]);
  const [howto, setHowto] = useState(false);
  const [howtoInteractive, setHowtoInteractive] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [topPlayers, setTopPlayers] = useState([]);
  const [difficulty, setDifficultyState] = useState(getDifficulty());
  const [mutedState, setMutedState] = useState(isMuted());
  const [streakOpen, setStreakOpen] = useState(false);
  const [streakInfo, setStreakInfo] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [signinOfferOpen, setSigninOfferOpen] = useState(false);
  const [bonusToast, setBonusToast] = useState(0); // chips just awarded
  const [quickPlayOpen, setQuickPlayOpen] = useState(false);
  const [quickPlayBusy, setQuickPlayBusy] = useState(false);
  const [skinPickerOpen, setSkinPickerOpen] = useState(false);
  const [cosmeticsTab, setCosmeticsTab] = useState("skins");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [outOfChipsOpen, setOutOfChipsOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [inviteFriendsOpen, setInviteFriendsOpen] = useState(false);
  // Active-room resume banner: backend tells us if the user is currently
  // seated in a room with status waiting/playing. Lets them rejoin after
  // closing the tab, navigating away, or refreshing the lobby. Null = none.
  const [activeRoom, setActiveRoom] = useState(null);

  // Single-player ante guard. Player must have ≥ this many chips to vs-bots.
  const SOLO_BET = 100;

  useEffect(() => {
    (async () => {
      // Track first landing (fires once per mount)
      try { track("lobby_viewed"); } catch {}
      // Wait for any async session rehydration (Capacitor Preferences fallback)
      // to complete BEFORE calling /auth/me. Without this gate, cold-start races
      // between the Bearer header being set and the first /auth/me call, causing
      // a spurious "Session expired" flash even when the user is fully signed in.
      try { await rehydrateReady; } catch {}
      // Try to restore auth session first; falls back to anonymous profile.
      // On Android cold start, /auth/me can transiently fail with "Network
      // Error" while the WebView's network stack is still warming up. We
      // retry once after a short delay so a transient blip doesn't get
      // misclassified as an expired session.
      let prof = null;
      let meErr = null;
      let me = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          me = await getMe();
          meErr = null;
          break;
        } catch (e) {
          meErr = e;
          // Real 401 → don't waste time retrying; session is truly invalid.
          if (e?.response?.status === 401) break;
          // Any other failure (Network Error, 5xx, etc.) → wait briefly and
          // retry once. Most Android cold-start failures recover within
          // 500ms once the WebView finishes initialising.
          if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
        }
      }
      if (me?.user) {
        setAuthUser(me.user);
        prof = me.profile;
        // Tie this browser to the signed-in user in PostHog for funnel stitching
        try {
          identify(me.user.user_id, {
            email: me.user.email,
            name: me.user.name,
          });
        } catch {}
      } else if (meErr) {
        // ONLY treat a genuine 401 as "session expired". Network Error,
        // timeouts, and 5xx are transient (or the user just isn't online
        // yet) and should NOT wipe the durable session token — doing so
        // forces the user to re-sign-in for what is really just a flaky
        // first request on Android cold start.
        const wasAuthed = (() => { try { return !!localStorage.getItem("quattro_authed"); } catch { return false; } })();
        if (wasAuthed && meErr?.response?.status === 401) {
          setSigninError("Session expired — please sign in again to access your account.");
          try {
            localStorage.removeItem("quattro_session_token");
            localStorage.removeItem("quattro_authed");
          } catch {}
        }
        // Otherwise: not signed in OR transient network blip — continue silently.
        // The in-memory axios Bearer header (set by rehydrateNativeSession)
        // remains intact, so subsequent calls (Profile, leaderboard, etc.)
        // will still authenticate correctly.
      }
      try {
        if (!prof) prof = await fetchOrCreateProfile(getStoredName());
        setProfile(prof);
        setNameDraft(prof.name);
      } catch (e) { console.error(e); }
      try { setTopPlayers(await fetchLeaderboard(5)); } catch {}
      // Active-room check — if the user is currently mid-game, surface a
      // resume banner. 404 (no active room) is expected and silent.
      try {
        const uid = getOrCreateUserId();
        const r = await axios.get(`${API}/profile/${uid}/active-room`);
        if (r?.data?.code) setActiveRoom(r.data);
      } catch {
        // No active room — quiet failure, nothing to show
      }
      try {
        const uid = getOrCreateUserId();
        const res = await axios.get(`${API}/streak/${uid}`);
        setStreakInfo(res.data);
        // Don't auto-open the streak modal right after a sign-in (the modal would
        // overlay the new account/sign-out pill), or when the first-time guest
        // signup offer is about to show (avoid double-modal stack), or if the
        // user hasn't seen the tutorial yet (first-visit = no streak modal
        // aggression — let them play 1 game first).
        const seenOffer = localStorage.getItem("quattro_seen_signup_offer") === "1";
        const isGuest = !localStorage.getItem("quattro_authed");
        const onCallback = window.location.hash.includes("session_id=");
        const offerWillShow = !seenOffer && isGuest && !onCallback;
        const tutorialSeen = hasSeenTutorial();
        // Don't auto-open before the user has played at least one session.
        // Instead they get the chip icon in the header and can open it manually.
        if (res.data.can_claim && !onCallback && !offerWillShow && tutorialSeen) {
          setTimeout(() => setStreakOpen(true), 3500);
        }
      } catch (e) { console.error(e); }

      // Bonus toast right after a sign-in that earned the +250.
      // Two sources: (a) legacy sessionStorage from old flow; (b) new OAuth
      // flow appends ?bonus=250 to the redirect URL after sign-in.
      try {
        const qs = new URLSearchParams(window.location.search);
        const urlBonus = parseInt(qs.get("bonus") || "0", 10);
        const stashedBonus = parseInt(sessionStorage.getItem("quattro_signup_bonus") || "0", 10);
        const bonus = urlBonus || stashedBonus;
        if (bonus > 0) {
          sessionStorage.removeItem("quattro_signup_bonus");
          setBonusToast(bonus);
          sfx.win();
          try { track("signup_bonus_awarded", { amount: bonus }); } catch {}
          setTimeout(() => setBonusToast(0), 4200);
        }
        // Strip auth-related query params from the URL after consuming them
        if (qs.has("signed_in") || qs.has("bonus") || qs.has("uid") || qs.has("auth_error")) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch {}

      // First-time guest offer: show once, only when not signed in.
      // Gated by tutorial-seen — we never stack two modals at first visit.
      try {
        const seen = localStorage.getItem("quattro_seen_signup_offer") === "1";
        const onCallback = window.location.hash.includes("session_id=");
        const isGuest = !localStorage.getItem("quattro_authed");
        const tutorialSeen = hasSeenTutorial();
        if (!seen && isGuest && !onCallback && tutorialSeen) {
          setTimeout(() => setSigninOfferOpen(true), 1400);
        }
      } catch {}

      // First-run tutorial — highest priority modal, shown once ever.
      try {
        if (!hasSeenTutorial() && !window.location.hash.includes("session_id=")) {
          setTimeout(() => setTutorialOpen(true), 500);
        }
      } catch {}

      // Auto-open the redeem modal if the user landed via an invite link
      // (?invite=ABC123). One-shot: marker stops it from re-opening.
      try {
        const u = new URL(window.location.href);
        const fromUrl = (u.searchParams.get("invite") || "").trim();
        if (fromUrl && shouldOfferRedeem()) {
          setTimeout(() => setRedeemOpen(true), 1200);
        }
      } catch {}
    })();
  }, []);

  const [signinError, setSigninError] = useState("");

  const signIn = async () => {
    sfx.click();
    setSigninError("");
    try { localStorage.setItem("quattro_seen_signup_offer", "1"); } catch {}
    // On Capacitor (Android/iOS), use the native in-app Google account picker
    // instead of redirecting to a browser tab. The plugin returns an idToken
    // which we exchange for a server session via /api/auth/google-native.
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    if (isNative) {
      const result = await signInWithGoogleNative();
      if (result.ok) {
        try { setAuthUser({ name: result.name, email: result.email, picture: result.picture, user_id: result.uid }); } catch {}
        if (result.bonus_amount) {
          try { localStorage.setItem("quattro_signin_bonus_amount", String(result.bonus_amount)); } catch {}
        }
        window.location.reload();
      } else {
        // Surface the actual failure reason instead of silently doing nothing.
        // Common reasons: missing Android OAuth client (Cloud Console),
        // SHA-1 mismatch, user cancelled, network error.
        const msg = result.reason === "no_id_token"
          ? "Google returned no token. Most likely the Android OAuth client isn't set up in Google Cloud Console (or the SHA-1 doesn't match)."
          : result.reason === "12501" || /cancel/i.test(result.reason || "")
            ? "" // user cancelled — don't show an error
            : `Sign-in failed: ${result.reason || "unknown error"}`;
        if (msg) setSigninError(msg);
        console.warn("Native sign-in cancelled or failed:", result.reason);
      }
      return;
    }
    const url = getEmergentRedirectUrl();
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = url;
        return;
      }
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    window.location.href = url;
  };

  const dismissSigninOffer = () => {
    try { localStorage.setItem("quattro_seen_signup_offer", "1"); } catch {}
    setSigninOfferOpen(false);
    // After dismissing the offer, surface the daily streak if it was suppressed.
    if (streakInfo?.can_claim) {
      setTimeout(() => setStreakOpen(true), 350);
    }
  };

  const signOut = async () => {
    sfx.click();
    // 1. Backend logout — clears the httpOnly session cookie (web flow)
    try { await logout(); } catch {/* ignore */}
    // 2. Native logout — wipes session_token / quattro_authed / quattro_user_id
    //    from BOTH localStorage AND @capacitor/preferences, plus signs out of
    //    the native Google account picker so the next sign-in shows the
    //    chooser (instead of silently re-authing). Without this, on Capacitor
    //    the rehydrate-on-reload would read Preferences and re-sign you in
    //    instantly — exactly the "signed back in immediately" bug.
    try { await signOutGoogleNative(); } catch {/* ignore */}
    setAuthUser(null);
    // Reload to reset to guest mode
    window.location.reload();
  };

  const saveName = async () => {
    if (!nameDraft.trim()) { setEditingName(false); return; }
    try {
      const p = await updateProfile({ name: nameDraft.trim() });
      setProfile(p);
      localStorage.setItem("quattro_user_name", p.name);
    } catch (e) { console.error(e); }
    setEditingName(false);
  };

  const pickDifficulty = (k) => {
    setDifficulty(k);
    setDifficultyState(k);
    sfx.click();
  };

  const toggleMute = () => {
    const next = !mutedState;
    setMuted(next);
    setMutedState(next);
    if (!next) sfx.click();
  };

  const handlePlay = () => {
    sfx.click();
    const liveChips = profileRef.current?.chips ?? profile?.chips ?? 0;
    if (liveChips < SOLO_BET) {
      setOutOfChipsOpen(true);
      try { track("out_of_chips_shown", { source: "play_vs_bots" }); } catch {}
      return;
    }
    try { track("game_started", { mode: "vs_bots", difficulty }); } catch {}
    nav("/play");
  };

  const startQuickPlay = async (bet) => {
    if (!profile) return;
    setQuickPlayBusy(true);
    try {
      const { quickPlay } = await import("../lib/matchmaking");
      const res = await quickPlay(profile.name, bet, profile.avatar || "default");
      sfx.click();
      setQuickPlayOpen(false);
      setQuickPlayBusy(false);
      try { track("game_started", { mode: "quickplay", bet, code: res.code }); } catch {}
      // Whether we joined an existing room or created a new public one,
      // route to the room screen — chat & host controls will work the same.
      nav(`/online/room/${res.code}`);
    } catch (e) {
      console.error(e);
      setQuickPlayBusy(false);
      try { track("quickplay_failed", { reason: e?.response?.data?.detail || "unknown" }); } catch {}
      alert(e?.response?.data?.detail || "Quick Play failed. Try again.");
    }
  };

  // Mount banner ad on Lobby; unmount on navigate-away OR if the user owns
  // an ACTIVE ad-free pass. Watches ads_removed_until from profile.
  useEffect(() => {
    if (isAdsRemovedActive(profile)) { unmountBanner(); return undefined; }
    if (!shouldShowBanner()) return undefined;
    mountBanner();
    return () => { unmountBanner(); };
  }, [profile?.ads_removed_until]);

  return (
    <div
      className="min-h-screen w-full px-4 py-6 md:py-16 flex flex-col items-center"
      style={{ paddingBottom: `${BANNER_HEIGHT_PX + 24}px` }}
    >
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-6 md:mb-10 flex-wrap">
          <div className="flex items-center gap-2 md:gap-3">
            <div
              className="font-display font-black text-xl md:text-3xl tracking-tight"
              onClick={() => {
                window._logoTaps = (window._logoTaps || 0) + 1;
                if (window._logoTaps >= 5) {
                  window._logoTaps = 0;
                  enableDebug();
                }
                setTimeout(() => { window._logoTaps = 0; }, 2500);
              }}
              data-testid="lobby-logo"
            >QUATTRO</div>
            <span className="label-eyebrow hidden md:inline">CARD WAR · 4 PLAYERS</span>
            <span
              className="text-white/30 font-mono"
              style={{ fontSize: 9 }}
              data-testid="lobby-version-badge"
              title={typeof window !== "undefined" ? `Backend: ${window.location.origin.includes("localhost") ? "REMOTE" : "ORIGIN"}` : ""}
            >v1.0.23</span>
          </div>
          {!profile && (
            <div
              className="glass rounded-full px-3 py-1.5 flex items-center gap-2"
              style={{
                border: "1px solid rgba(255,200,0,0.45)",
                fontSize: 11,
              }}
              data-testid="profile-fallback-pill"
            >
              <span className="text-white/80">Profile didn't load</span>
              <button
                className="rounded-full px-2 py-0.5 font-bold"
                style={{ background: "#FFD700", color: "#0B0C10", fontSize: 10 }}
                onClick={() => {
                  try {
                    // Wipe corrupt session state but keep gameplay
                    // identity if it exists (anon u_xxx will be auto-regenerated).
                    ["quattro_session_token","quattro_authed","quattro_user_name"].forEach(k=>localStorage.removeItem(k));
                  } catch {}
                  window.location.reload();
                }}
                data-testid="profile-reset-btn"
              >RESET</button>
            </div>
          )}
          {profile && (
            <>
            <div className="flex items-center gap-1.5 md:gap-3 flex-wrap justify-end">
              <button
                className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5 hover:bg-white/10"
                onClick={() => { sfx.click(); setStreakOpen(true); }}
                data-testid="streak-pill"
                style={{
                  border: `1px solid ${streakInfo?.can_claim ? "rgba(255,215,0,0.5)" : "rgba(255,255,255,0.12)"}`,
                  boxShadow: streakInfo?.can_claim ? "0 0 14px rgba(255,215,0,0.35)" : "none",
                  minHeight: 32,
                }}
                title="Daily Streak"
              >
                <span style={{ fontSize: 13 }}>🔥</span>
                <span className="font-mono font-bold" style={{ fontSize: 11 }}>
                  {streakInfo?.current_streak || 0}
                </span>
                {streakInfo?.can_claim && (
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%", background: "#FFD700",
                    boxShadow: "0 0 6px #FFD700",
                  }} />
                )}
              </button>
              <div
                className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5 md:gap-2"
                data-testid="chips-pill"
                title="Chips — earned by playing. Bet to enter games."
                style={{ minHeight: 32 }}
              >
                <span style={{ color: "#FFD700", fontSize: 14 }}>◆</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{profile.chips.toLocaleString()}</span>
              </div>
              <button
                className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5 hover:bg-white/10"
                onClick={() => { sfx.click(); nav("/store"); }}
                data-testid="gems-pill"
                style={{ border: "1px solid rgba(0,229,255,0.45)", minHeight: 32 }}
                title="Gems — premium currency. Unlock skins, re-rolls, hints. Tap to buy more."
              >
                <span style={{ color: "#00E5FF", fontSize: 13 }}>◈</span>
                <span className="font-mono font-bold" style={{ fontSize: 11 }}>{(profile.gems || 0).toLocaleString()}</span>
                <span className="label-eyebrow" style={{ fontSize: 9, color: "#00E5FF" }}>+</span>
              </button>
              <button
                className="glass rounded-full px-2.5 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5 hover:bg-white/10"
                onClick={() => { sfx.click(); setCosmeticsTab("skins"); setSkinPickerOpen(true); }}
                data-testid="skin-pill"
                title="Cosmetics — card-back skins, victory FX, avatar frames"
                style={{ border: "1px solid rgba(255,255,255,0.14)", minHeight: 32 }}
              >
                <span style={{ fontSize: 12 }}>🎨</span>
                <span className="font-display font-bold hidden sm:inline" style={{ fontSize: 11 }}>Cosmetics</span>
              </button>
              <WatchAdButton onCredited={async () => {
                // Refresh profile so the chips pill updates immediately
                try { setProfile(await fetchOrCreateProfile(getStoredName())); } catch {}
              }} />
              <div className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 hidden sm:flex items-center gap-2" data-testid="level-pill" style={{ minHeight: 32 }}>
                <span className="label-eyebrow" style={{ fontSize: 10 }}>LV</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{profile.level}</span>
              </div>
              <button
                className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 flex items-center gap-2 hover:bg-white/10"
                onClick={toggleMute}
                data-testid="mute-toggle"
                title={mutedState ? "Unmute" : "Mute"}
                style={{ border: "1px solid rgba(255,255,255,0.12)", minHeight: 32 }}
              >
                <span style={{ fontSize: 13 }}>{mutedState ? "🔇" : "🔊"}</span>
              </button>
              {authUser ? (
                <div className="glass rounded-full pl-1.5 pr-2.5 md:pl-2 md:pr-3 py-1 flex items-center gap-2" data-testid="account-pill" style={{ minHeight: 32 }}>
                  {authUser.picture ? (
                    <img src={authUser.picture} alt="" className="rounded-full" style={{ width: 24, height: 24 }} referrerPolicy="no-referrer" />
                  ) : (
                    <div className="rounded-full flex items-center justify-center"
                      style={{ width: 24, height: 24, background: "#FFD700", color: "#0B0C10", fontWeight: 900, fontSize: 11, letterSpacing: 0.5 }}>
                      {getInitials(authUser.name)}
                    </div>
                  )}
                  <span className="font-display font-bold hidden sm:inline" style={{ fontSize: 12 }}>{authUser.name?.split(" ")[0] || "You"}</span>
                  <button
                    onClick={signOut}
                    className="text-white/55 hover:text-white text-xs underline ml-1"
                    data-testid="sign-out-btn"
                  >Sign out</button>
                </div>
              ) : (
                <button
                  className="rounded-full px-3 py-1.5 md:px-4 md:py-2 flex items-center gap-2 font-bold"
                  onClick={signIn}
                  data-testid="sign-in-btn"
                  style={{
                    background: "#fff", color: "#0B0C10", fontSize: 12, minHeight: 32,
                    boxShadow: "0 0 18px rgba(255,255,255,0.25)",
                    border: "1px solid rgba(255,255,255,0.5)",
                  }}
                  title="Sign in to save chips across devices"
                >
                  <svg width="14" height="14" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  <span className="hidden sm:inline">Sign in</span>
                </button>
              )}
            </div>
            {signinError && (
              <div
                className="text-[11px] mt-1.5 ml-2 w-full text-right max-w-[420px]"
                style={{ color: "#FF8888" }}
                data-testid="signin-error"
              >
                {signinError}
              </div>
            )}
            </>
          )}
        </div>

        {/* Daily-deal banner — opens cosmetics at the right tab */}
        <DailyDealBanner
          onOpen={(tab) => {
            sfx.click();
            setCosmeticsTab(tab);
            setSkinPickerOpen(true);
          }}
        />

        {/* Hero */}
        <div className="grid md:grid-cols-12 gap-4 md:gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="md:col-span-7 glass rounded-3xl p-6 md:p-12 relative overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(181,55,242,0.35), transparent 60%)" }} />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(0,229,255,0.3), transparent 60%)" }} />
            <div className="label-eyebrow mb-3">A 4-PLAYER CARD STRATEGY</div>
            {/* Active-game resume banner — shown only when the user has an
                in-progress room they wandered away from. Tapping rejoins
                straight into the right page (waiting room or live game). */}
            {activeRoom && (
              <button
                onClick={() => {
                  sfx.click();
                  const code = activeRoom.code;
                  const dest = activeRoom.status === "playing"
                    ? `/online/play/${code}`
                    : `/online/room/${code}`;
                  nav(dest);
                }}
                className="rounded-2xl px-4 py-3 mb-5 flex items-center gap-3 w-full text-left transition-all hover:translate-y-[-1px]"
                style={{
                  background: "linear-gradient(135deg, rgba(0,229,255,0.10), rgba(181,55,242,0.10))",
                  border: "1.5px solid rgba(0,229,255,0.55)",
                  boxShadow: "0 0 18px rgba(0,229,255,0.25)",
                }}
                data-testid="resume-game-banner"
              >
                <span style={{ fontSize: 22 }}>🎮</span>
                <div className="flex-1">
                  <div className="font-display font-black text-sm" style={{ color: "#00E5FF" }}>
                    {activeRoom.status === "playing" ? "GAME IN PROGRESS" : "ROOM WAITING"}
                  </div>
                  <div className="text-xs text-white/65">
                    Room <b className="font-mono">{activeRoom.code}</b>
                    {activeRoom.tournament_id && <> · 🏆 Tournament</>}
                    {activeRoom.casual && !activeRoom.tournament_id && <> · 🤝 Friend match</>}
                    <span className="text-white/45"> · tap to rejoin</span>
                  </div>
                </div>
                <span className="font-display font-black text-xs" style={{ color: "#00E5FF" }}>
                  REJOIN →
                </span>
              </button>
            )}
            <h1 className="font-display font-black leading-none" style={{ fontSize: "clamp(40px, 7vw, 86px)" }}>
              Line up<br />
              <span style={{
                background: "linear-gradient(95deg, #FFD700, #00E5FF, #FF2A2A, #B537F2)",
                WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
              }}>FOUR</span><br />
              of yours.
            </h1>
            <p className="text-white/70 mt-5 max-w-md">
              A 6×6 grid. Four colors. Power-stack, block, and outwit three bots to make a line of four.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button className="btn-primary" onClick={handlePlay} data-testid="play-now-btn">
                Play vs Bots
              </button>
              <button className="btn-ghost" onClick={() => { sfx.click(); nav("/online/new"); }} data-testid="online-new-btn">
                🤝 Friend Match (Casual)
              </button>
              <button className="btn-ghost" onClick={() => { sfx.click(); setQuickPlayOpen(true); }} data-testid="quick-play-btn">
                Quick Play
              </button>
              <button className="btn-ghost" onClick={() => { sfx.click(); nav("/tournaments"); }} data-testid="tournaments-btn">
                Tournaments
              </button>
              <button className="btn-ghost" onClick={() => { sfx.click(); nav("/online/join"); }} data-testid="online-join-btn">
                Join with Code
              </button>
              <button className="btn-ghost" onClick={() => { sfx.click(); setHowtoInteractive(true); }} data-testid="howto-btn">How to Play</button>
              <button className="btn-ghost" onClick={() => { sfx.click(); nav("/leaderboard"); }} data-testid="leaderboard-btn">
                Leaderboard
              </button>
              <button className="btn-ghost" onClick={() => { sfx.click(); nav("/profile"); }} data-testid="profile-btn">
                My Profile
              </button>
              {shouldOfferRedeem() && (
                <button
                  className="btn-ghost"
                  onClick={() => { sfx.click(); setRedeemOpen(true); }}
                  data-testid="redeem-invite-btn"
                  style={{
                    border: "1px solid rgba(255,215,0,0.45)",
                    color: "#FFD700",
                  }}
                >🎁 Got an invite?</button>
              )}
              <button
                className="btn-ghost"
                onClick={() => {
                  sfx.click();
                  try { track("invite_friends_opened"); } catch {}
                  setInviteFriendsOpen(true);
                }}
                data-testid="invite-friends-btn"
                style={{
                  border: "1px solid rgba(61,220,151,0.45)",
                  color: "#3DDC97",
                }}
              >🤝 Invite friends</button>
            </div>
            {/* Difficulty selector */}
            <div className="mt-6">
              <div className="label-eyebrow mb-2">Bot Difficulty</div>
              <div className="flex flex-wrap gap-2" data-testid="difficulty-row">
                {DIFFICULTIES.map((d) => {
                  const active = difficulty === d.key;
                  return (
                    <button
                      key={d.key}
                      className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all"
                      onClick={() => pickDifficulty(d.key)}
                      data-testid={`difficulty-${d.key}`}
                      style={{
                        background: active ? "#fff" : "rgba(255,255,255,0.06)",
                        color: active ? "#000" : "#fff",
                        border: `1px solid ${active ? "#fff" : "rgba(255,255,255,0.15)"}`,
                        boxShadow: active ? "0 0 18px rgba(255,255,255,0.35)" : "none",
                        cursor: "pointer",
                      }}
                      title={d.desc}
                    >{d.label}</button>
                  );
                })}
              </div>
              <div className="text-xs text-white/55 mt-2" data-testid="difficulty-desc">
                {DIFFICULTIES.find(d => d.key === difficulty)?.desc}
              </div>
            </div>
          </motion.div>

          {/* Profile + suits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-5 flex flex-col gap-6"
          >
            <div className="glass rounded-3xl p-6">
              <div className="label-eyebrow mb-3">Profile</div>
              <div className="flex items-center gap-4">
              <AvatarFrame frame={profile?.active_avatar_frame} size={64} radius="1rem">
                <div className="rounded-2xl flex items-center justify-center font-display font-black w-full h-full"
                  style={{
                    background: "linear-gradient(155deg, #FFD700, #00E5FF, #B537F2)",
                    color: "#0B0C10", fontSize: 22, letterSpacing: 1, borderRadius: "1rem",
                  }}
                  data-testid="profile-avatar"
                >
                  {getInitials(profile?.name || "P")}
                </div>
              </AvatarFrame>
                <div className="flex-1">
                  {editingName ? (
                    <div className="flex gap-2">
                      <input
                        className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white outline-none w-full"
                        value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                        maxLength={24}
                        data-testid="name-input"
                      />
                      <button className="btn-ghost" style={{ padding: "8px 14px" }} onClick={saveName} data-testid="save-name-btn">Save</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="font-display font-black text-xl" data-testid="profile-name">{profile?.name || "Loading..."}</div>
                      <button className="text-white/50 hover:text-white text-xs underline"
                        onClick={() => { setNameDraft(profile?.name || ""); setEditingName(true); }}
                        data-testid="edit-name-btn"
                      >edit</button>
                    </div>
                  )}
                  <div className="flex gap-4 mt-1 text-xs text-white/60 font-mono">
                    <span>W <b className="text-white">{profile?.wins ?? 0}</b></span>
                    <span>L <b className="text-white">{profile?.losses ?? 0}</b></span>
                    <span>XP <b className="text-white">{profile?.xp ?? 0}</b></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass rounded-3xl p-6">
              <div className="label-eyebrow mb-4">The Four Suits</div>
              <div className="grid grid-cols-4 gap-3">
                {PLAYERS.map((p) => (
                  <div key={p.id} className="text-center">
                    <div
                      className="rounded-2xl flex items-center justify-center font-display font-black mx-auto"
                      style={{
                        width: 52, height: 52,
                        background: `linear-gradient(155deg, ${p.color}, #111)`,
                        color: p.id === 0 ? "#0B0C10" : "#fff",
                        boxShadow: `0 0 18px ${p.glow}`,
                        fontSize: 22,
                        border: `1px solid ${p.color}`,
                      }}
                    >{p.suit}</div>
                    <div className="label-eyebrow mt-2" style={{ fontSize: 9 }}>{p.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mini leaderboard */}
        <div className="glass rounded-3xl p-6 mt-6" data-testid="mini-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow">Top Superstars</div>
            <button className="text-white/60 text-xs underline" onClick={() => nav("/leaderboard")} data-testid="view-all-lb-btn">view all</button>
          </div>
          {topPlayers.length === 0 ? (
            <div className="text-white/50 text-sm">No players yet — be the first.</div>
          ) : (
            <div className="space-y-2">
              {topPlayers.map((tp, i) => (
                <div key={tp.user_id} className="flex items-center justify-between text-sm" data-testid={`mini-lb-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono w-6 text-white/50">#{i + 1}</span>
                    <span className="font-display font-bold">{tp.name}</span>
                    <span className="label-eyebrow" style={{ fontSize: 9 }}>LV {tp.level}</span>
                  </div>
                  <span className="font-mono font-bold" style={{ color: "#FFD700" }}>◆ {tp.chips.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-white/45">
          <a href="/privacy" className="hover:text-white/80 underline" data-testid="footer-privacy">Privacy Policy</a>
          <span>·</span>
          <a href="/terms" className="hover:text-white/80 underline" data-testid="footer-terms">Terms of Service</a>
          <span>·</span>
          <a href="mailto:support@quattro.app" className="hover:text-white/80 underline">Contact</a>
          <span>·</span>
          <span>© 2026 Quattro Games</span>
        </footer>
      </div>

      <HowToPlay open={howto} onClose={() => setHowto(false)} />
      <InteractiveTutorial
        open={howtoInteractive}
        onClose={() => setHowtoInteractive(false)}
        autoplayOnFinish={false}
      />
      <RedeemInviteModal
        open={redeemOpen}
        onClose={() => {
          setRedeemOpen(false);
          markRedeemed(); // Skip dismissal also counts so the prompt doesn't badger them
        }}
        onSuccess={async (res) => {
          // Refresh profile to surface the new chip balance
          try { setProfile(await fetchOrCreateProfile(getStoredName())); } catch {}
          setBonusToast(res.chips_credited);
          setTimeout(() => setBonusToast(null), 3000);
          try { track("invite_redeemed", { chips: res.chips_credited }); } catch {}
        }}
      />
      <InviteFriendsModal
        open={inviteFriendsOpen}
        onClose={() => setInviteFriendsOpen(false)}
      />
      <OutOfChipsModal
        open={outOfChipsOpen}
        required={SOLO_BET}
        current={profile?.chips ?? 0}
        onClose={() => setOutOfChipsOpen(false)}
        onCredited={(chips) => {
          setProfile((p) => p ? { ...p, chips: (p.chips || 0) + chips } : p);
        }}
        onOpenStore={() => nav("/store")}
      />
      <AdFreeUpsellModal
        profile={profile}
        onPurchase={() => nav("/store")}
      />
      <InteractiveTutorial
        open={tutorialOpen}
        autoplayOnFinish={false}
        onClose={() => {
          setTutorialOpen(false);
          // After tutorial ends, chain the sign-up offer for first-time guests.
          try {
            const seen = localStorage.getItem("quattro_seen_signup_offer") === "1";
            const isGuest = !localStorage.getItem("quattro_authed");
            if (!seen && isGuest && !authUser) {
              setTimeout(() => setSigninOfferOpen(true), 350);
            }
          } catch {}
        }}
      />
      <DailyStreak
        open={streakOpen}
        onClose={() => setStreakOpen(false)}
        onClaimed={(p) => {
          setProfile(p);
          setStreakInfo((s) => s ? { ...s, can_claim: false, current_streak: p.current_streak } : s);
        }}
      />
      <SignInOffer
        open={signinOfferOpen && !authUser}
        onClose={dismissSigninOffer}
        onSignIn={signIn}
        bonus={250}
      />
      <CosmeticsModal
        open={skinPickerOpen}
        profile={profile}
        initialTab={cosmeticsTab}
        onClose={() => setSkinPickerOpen(false)}
        onApplied={(p) => setProfile(p)}
      />
      {quickPlayOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center px-4"
          data-testid="quick-play-modal"
        >
          <div
            className="absolute inset-0"
            style={{ background: "rgba(5,5,7,0.78)", backdropFilter: "blur(14px)" }}
            onClick={() => !quickPlayBusy && setQuickPlayOpen(false)}
          />
          <motion.div
            initial={{ y: 12, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative glass rounded-3xl p-6 max-w-md w-full"
            style={{ border: "1px solid rgba(0,229,255,0.4)" }}
          >
            <div className="label-eyebrow mb-2" style={{ color: "#00E5FF" }}>QUICK PLAY</div>
            <h2 className="font-display font-black text-2xl mb-1">Find an opponent</h2>
            <p className="text-white/70 text-sm mb-5">
              We'll match you with the next available room at this stake — or
              spin up a public one and seat you as host.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[50, 100, 250, 1000].map((b) => {
                const canAfford = (profile?.chips || 0) >= b;
                return (
                  <button
                    key={b}
                    disabled={!canAfford || quickPlayBusy}
                    onClick={() => startQuickPlay(b)}
                    className="rounded-2xl p-4 text-center"
                    style={{
                      background: canAfford ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.02)",
                      border: `1px solid ${canAfford ? "rgba(0,229,255,0.35)" : "rgba(255,255,255,0.08)"}`,
                      cursor: canAfford ? "pointer" : "not-allowed",
                      opacity: quickPlayBusy ? 0.6 : 1,
                    }}
                    data-testid={`quick-play-bet-${b}`}
                  >
                    <div className="font-display font-black text-2xl" style={{ color: "#FFD700" }}>◆ {b}</div>
                    <div className="label-eyebrow mt-1" style={{ fontSize: 9 }}>
                      {canAfford ? `POT ◆ ${b * 4}` : "INSUFFICIENT CHIPS"}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-white/55 text-center">
              Your chips: <b style={{ color: "#FFD700" }}>◆{(profile?.chips || 0).toLocaleString()}</b>
            </div>
            <button
              onClick={() => setQuickPlayOpen(false)}
              disabled={quickPlayBusy}
              className="absolute top-3 right-4 text-white/55 hover:text-white text-xs underline"
              data-testid="quick-play-close-btn"
            >Close</button>
            {quickPlayBusy && (
              <div className="text-center text-xs text-white/65 mt-4" data-testid="quick-play-busy">
                Finding a room…
              </div>
            )}
          </motion.div>
        </div>
      )}
      {bonusToast > 0 && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          className="fixed left-1/2 z-[130] glass rounded-full px-5 py-3 flex items-center gap-3"
          style={{
            top: 24,
            transform: "translateX(-50%)",
            border: "1px solid rgba(255,215,0,0.55)",
            boxShadow: "0 0 28px rgba(255,215,0,0.35)",
          }}
          data-testid="bonus-toast"
        >
          <span style={{ fontSize: 18 }}>◆</span>
          <span className="font-display font-bold text-sm">
            Welcome bonus claimed: <span style={{ color: "#FFD700" }}>+{bonusToast} chips</span>
          </span>
        </motion.div>
      )}
      <DebugOverlay />
    </div>
  );
}
